import { GoogleGenAI, Type } from "@google/genai";
import { NodeType } from '../types.js';

// Use REACT_APP_ prefix for environment variables in Create React App
const API_KEY = process.env.REACT_APP_GEMINI_API_KEY;
if (!API_KEY) {
  throw new Error("REACT_APP_GEMINI_API_KEY environment variable not set");
}

const ai = new GoogleGenAI({ apiKey: API_KEY });

// Add custom node types to availableNodeTypes array
const availableNodeTypes = [...Object.values(NodeType), "NOVEL_NODE", "TODO_LIST", "TODO_VIEW", "DOCUMENT_NODE", "DELEGATION_NODE"];

const enhancedWorkflowSchema = {
  type: Type.OBJECT,
  properties: {
    nodes: {
      type: Type.ARRAY,
      items: {
        type: Type.OBJECT,
        properties: {
          type: {
            type: Type.STRING,
            enum: [...availableNodeTypes, "NOVEL_NODE", "TODO_LIST", "TODO_VIEW", "DOCUMENT_NODE", "DELEGATION_NODE", "COMMENT"],
            description: 'The type of the workflow node.',
          },
          label: {
            type: Type.STRING,
            description: 'A concise, user-friendly label for what this node does.',
          },
          assignedTo: {
            type: Type.STRING,
            description: 'Role or person responsible for this task (e.g., "Founder", "Legal Team", "Marketing Manager").',
          },
          priority: {
            type: Type.STRING,
            enum: ["CRITICAL", "HIGH", "MEDIUM", "LOW"],
            description: 'Priority level of this task.',
          },
          estimatedDuration: {
            type: Type.STRING,
            description: 'Estimated time to complete (e.g., "2 weeks", "3 days", "1 month").',
          },
          dependencies: {
            type: Type.ARRAY,
            items: { type: Type.STRING },
            description: 'Labels of nodes that must be completed before this one.',
          },
          requiredDocuments: {
            type: Type.ARRAY,
            items: { type: Type.STRING },
            description: 'List of documents needed for this step.',
          },
          todoItems: {
            type: Type.ARRAY,
            items: {
              type: Type.OBJECT,
              properties: {
                task: { type: Type.STRING },
                assignedTo: { type: Type.STRING },
                deadline: { type: Type.STRING },
                completed: { type: Type.BOOLEAN, default: false }
              },
            },
            description: 'Subtasks for TODO_LIST nodes.',
          },
          successCriteria: {
            type: Type.STRING,
            description: 'How to measure completion of this step.',
          },
          phase: {
            type: Type.STRING,
            enum: ["PLANNING", "FOUNDATION", "DEVELOPMENT", "LAUNCH", "GROWTH", "SCALING"],
            description: 'Business development phase.',
          },
          text: {
            type: Type.STRING,
            description: 'Text content for COMMENT nodes.',
          },
          items: {
            type: Type.ARRAY,
            items: {
              type: Type.OBJECT,
              properties: {
                text: { type: Type.STRING },
                completed: { type: Type.BOOLEAN, default: false },
                assignedTo: { type: Type.STRING },
                deadline: { type: Type.STRING }
              },
            },
            description: 'Todo items for TODO_LIST nodes.',
          }
        },
        required: ['type', 'label'],
      },
    },
    projectMetadata: {
      type: Type.OBJECT,
      properties: {
        totalEstimatedTime: { type: Type.STRING },
        criticalPath: {
          type: Type.ARRAY,
          items: { type: Type.STRING },
        },
        keyMilestones: {
          type: Type.ARRAY,
          items: {
            type: Type.OBJECT,
            properties: {
              name: { type: Type.STRING },
              targetDate: { type: Type.STRING },
              deliverables: {
                type: Type.ARRAY,
                items: { type: Type.STRING },
              },
            },
          },
        },
        teamRoles: {
          type: Type.ARRAY,
          items: {
            type: Type.OBJECT,
            properties: {
              role: { type: Type.STRING },
              responsibilities: {
                type: Type.ARRAY,
                items: { type: Type.STRING },
              },
              requiredSkills: {
                type: Type.ARRAY,
                items: { type: Type.STRING },
              },
            },
          },
        },
        documentChecklist: {
          type: Type.ARRAY,
          items: {
            type: Type.OBJECT,
            properties: {
              category: { type: Type.STRING },
              documents: {
                type: Type.ARRAY,
                items: { type: Type.STRING },
              },
            },
          },
        },
      },
    },
  },
  required: ['nodes', 'projectMetadata'],
};

const singleNodeSchema = {
  type: Type.OBJECT,
  properties: {
    type: {
      type: Type.STRING,
      enum: availableNodeTypes,
      description: 'The single most appropriate node type for the user request.',
    },
    label: {
      type: Type.STRING,
      description: 'A concise, user-friendly label for what this node does.',
    },
    assignedTo: {
      type: Type.STRING,
      description: 'Role or person responsible for this task.',
    },
    priority: {
      type: Type.STRING,
      enum: ["CRITICAL", "HIGH", "MEDIUM", "LOW"],
      description: 'Priority level of this task.',
    },
    estimatedDuration: {
      type: Type.STRING,
      description: 'Estimated time to complete.',
    },
    requiredDocuments: {
      type: Type.ARRAY,
      items: { type: Type.STRING },
      description: 'List of documents needed for this step.',
    },
  },
  required: ['type', 'label', 'assignedTo', 'priority'],
};

// New function to stream workflow generation
export async function* streamWorkflowFromPrompt(prompt, onNodeReceived) {
  // Convert the prompt to JSON format for better structure and clarity
  const jsonPrompt = {
    role: "system",
    content: {
      expertise: "business strategist, project manager, process automation specialist",
      task: "Create a COMPREHENSIVE, DETAILED, ERROR-FREE business execution plan",
      structure_requirements: [
        "Create 10-15 granular, actionable nodes organized into clear phases (PLANNING, FOUNDATION, DEVELOPMENT, LAUNCH, GROWTH, SCALING)",
        "Each node must be atomic - one clear action, not multiple combined tasks",
        "Establish clear dependencies showing which tasks must be completed before others",
        "Include realistic timelines and resource estimates",
        "Add COMMENT nodes to explain complex workflow sections and provide context",
        "Include TODO_LIST nodes with specific actionable items for parallel tasks that are directly related to the business plan",
        "Organize nodes in a logical flow with clear progression from one phase to the next"
      ],
      delegation_system: {
        role_assignment: "Assign each task to a specific role (e.g., 'Founder', 'Legal Counsel', 'Marketing Manager', 'Financial Advisor', 'HR Manager', 'Tech Lead', 'Sales Director')",
        priority_levels: ["CRITICAL", "HIGH", "MEDIUM", "LOW"],
        duration_estimation: "Include estimated duration for each task (be realistic: days, weeks, or months)",
        complex_node_handling: "For complex nodes, break down into subtasks with individual assignments"
      },
      document_requirements: {
        categories: {
          legal: ["Business licenses", "permits", "EIN", "operating agreements", "contracts", "NDAs"],
          financial: ["Business plan", "financial projections", "budget", "invoices", "tax forms"],
          hr: ["Job descriptions", "employment contracts", "onboarding materials", "policies"],
          marketing: ["Brand guidelines", "content calendar", "marketing plan", "campaign materials"],
          operational: ["SOPs", "vendor agreements", "inventory lists", "quality checklists"],
          compliance: ["Insurance policies", "health permits", "safety certifications"]
        }
      },
      todo_breakdown: {
        requirements: [
          "Clear, actionable task description",
          "Assigned role/person",
          "Realistic deadline",
          "Success criteria",
          "Mark completed tasks with completed: true"
        ],
        examples: [
          "Research 3 potential suppliers for raw materials by next Friday",
          "Schedule meeting with legal counsel to review partnership agreement",
          "Create social media accounts on Facebook, Instagram, and LinkedIn",
          "Set up project management tool and invite team members",
          "Prepare presentation deck for investor meeting"
        ]
      },
      available_node_types: availableNodeTypes,
      node_type_descriptions: {
        MARKET_RESEARCH: "Industry analysis, competitor research, customer surveys",
        REGISTER_BUSINESS: "Legal entity formation, registration, business structure",
        COMPLIANCE_CHECK: "Permits, licenses, regulatory requirements, insurance",
        HIRE_EMPLOYEE: "Recruitment, onboarding, team building",
        CREATE_WEBSITE: "Online presence, e-commerce, digital infrastructure",
        SOCIAL_POST: "Marketing campaigns, social media, content creation",
        MANAGE_INVOICES: "Financial operations, accounting, payment systems",
        EXPAND_BUSINESS: "Growth initiatives, scaling, new markets",
        TODO_LIST: "Groups of parallel tasks that can be done simultaneously with specific todo items that are directly related to the current workflow",
        DOCUMENT_NODE: "Document preparation, review, and organization",
        DELEGATION_NODE: "Task assignment and team coordination",
        NOVEL_NODE: "Innovative or unique elements specific to the business",
        COMMENT: "Explanatory notes that describe workflow sections, provide context, or explain complex decisions"
      },
      project_metadata: {
        total_estimated_timeline: "Calculate total estimated timeline",
        critical_path: "Identify critical path (sequence of tasks that determines minimum project duration)",
        key_milestones: "Define key milestones with target dates and deliverables",
        team_roles: "List all team roles with responsibilities and required skills",
        document_checklist: "Create comprehensive document checklist organized by category"
      },
      quality_standards: {
        label_format: "Each label must be a specific action statement (e.g., 'File Articles of Incorporation with State' not 'Legal Setup')",
        success_criteria: "Success criteria must be measurable (e.g., '50 customer interviews completed' not 'understand market')",
        dependencies: "Dependencies must be logical and explicit",
        timeline: "Timeline must account for approval processes, waiting periods, and realistic work capacity",
        comment_nodes: "Use COMMENT nodes to explain workflow sections, complex decisions, and provide context for better understanding",
        todo_lists: "Include TODO_LIST nodes with specific actionable items that can be tracked in the TODO panel and are directly related to the business plan"
      },
      error_free_workflow_requirements: {
        triggers_and_connections: "Ensure all nodes have proper triggers and connections",
        dependency_validation: "Validate that all dependencies are correctly specified",
        required_fields: "Confirm that all required fields are filled in",
        node_types: "Check that all node types are valid and appropriate for the task",
        workflow_completeness: "Verify that the workflow is complete and executable from start to finish",
        circular_dependencies: "Ensure there are no circular dependencies",
        team_roles: "Make sure all team roles are clearly defined",
        document_requirements: "Confirm that all document requirements are realistic and necessary",
        comment_usage: "Include COMMENT nodes to explain workflow logic and provide context",
        todo_inclusion: "Include relevant TODO_LIST nodes with actionable items for parallel tasks that are directly related to the current workflow"
      }
    }
  };

  const fullPrompt = `
${JSON.stringify(jsonPrompt, null, 2)}

User Request: "${prompt}"

Create a complete, executable, ERROR-FREE business plan that someone could follow step-by-step to build this business from concept to operation.

VERY IMPORTANT: Respond with a JSON object containing an array of nodes. Each node should be a separate JSON object in the array. I need you to format your response as a valid JSON array of node objects.

For TODO_LIST nodes, make sure to include specific, actionable todo items that are directly related to the business plan. For example, if the business is about opening a restaurant, include todo items like "Research local health department regulations", "Create menu with pricing", "Hire kitchen staff", etc.

For COMMENT nodes, provide helpful explanations about workflow sections or complex decisions.
`;

  try {
    console.log("Calling AI model with prompt:", prompt.substring(0, 100) + "...");
    
    // For streaming, we'll generate nodes one by one
    // This is a simplified approach - in a real implementation, we would use actual streaming
    const response = await ai.models.generateContent({
      model: "gemini-2.5-flash",
      contents: fullPrompt,
      config: {
        responseMimeType: "application/json",
        responseSchema: enhancedWorkflowSchema,
      },
    });

    console.log("Received response from AI model");
    
    const jsonString = response.text.trim();
    console.log("Response text:", jsonString.substring(0, 200) + "...");
    
    const parsedResponse = JSON.parse(jsonString);

    if (!parsedResponse.nodes || !Array.isArray(parsedResponse.nodes)) {
      throw new Error("AI response does not contain a valid nodes array.");
    }

    // Validate and yield nodes one by one
    for (const item of parsedResponse.nodes) {
      if (
        item && typeof item === 'object' && 
        'type' in item && 
        'label' in item && 
        (availableNodeTypes.includes(item.type) || item.type === 'COMMENT')
      ) {
        // Yield the validated node
        const baseNode = {
          type: item.type,
          label: item.label,
          phase: item.phase || "PLANNING"
        };
        
        // Add type-specific fields
        if (item.type === 'COMMENT') {
          baseNode.text = item.text || item.label;
        } else if (item.type === 'TODO_LIST') {
          // Process TODO items for TODO_LIST nodes
          if (item.items && Array.isArray(item.items)) {
            baseNode.items = item.items.map(todo => ({
              text: todo.text || '',
              completed: todo.completed || false,
              assignedTo: todo.assignedTo || '',
              deadline: todo.deadline || ''
            }));
          } else if (item.todoItems && Array.isArray(item.todoItems)) {
            baseNode.items = item.todoItems.map(todo => ({
              text: todo.task || todo.text || '',
              completed: todo.completed || false,
              assignedTo: todo.assignedTo || '',
              deadline: todo.deadline || ''
            }));
          } else {
            baseNode.items = [];
          }
        } else if (item.assignedTo) {
          baseNode.assignedTo = item.assignedTo;
          baseNode.priority = item.priority || "MEDIUM";
          baseNode.estimatedDuration = item.estimatedDuration || "";
          baseNode.requiredDocuments = item.requiredDocuments || [];
          baseNode.todoItems = item.todoItems || [];
          baseNode.successCriteria = item.successCriteria || "";
        }
        
        yield baseNode;
        
        // Call the callback if provided
        if (onNodeReceived) {
          onNodeReceived(baseNode);
        }
        
        // Small delay to simulate streaming
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }

    return {
      metadata: parsedResponse.projectMetadata || {},
    };
  } catch (error) {
    console.error("Error generating workflow from prompt:", error);
    // Provide more detailed error information
    if (error.message.includes("API key")) {
      throw new Error("Invalid API key. Please check your Gemini API configuration.");
    } else if (error.message.includes("quota")) {
      throw new Error("API quota exceeded. Please try again later or check your Gemini API plan.");
    } else if (error.message.includes("parse")) {
      throw new Error("Failed to parse AI response. The response format was unexpected.");
    } else {
      throw new Error(`Failed to communicate with the AI model: ${error.message}`);
    }
  }
}

export async function generateWorkflowFromPrompt(prompt) {
  // Convert the prompt to JSON format for better structure and clarity
  const jsonPrompt = {
    role: "system",
    content: {
      expertise: "business strategist, project manager, process automation specialist",
      task: "Create a COMPREHENSIVE, DETAILED, ERROR-FREE business execution plan",
      structure_requirements: [
        "Create 10-15 granular, actionable nodes organized into clear phases (PLANNING, FOUNDATION, DEVELOPMENT, LAUNCH, GROWTH, SCALING)",
        "Each node must be atomic - one clear action, not multiple combined tasks",
        "Establish clear dependencies showing which tasks must be completed before others",
        "Include realistic timelines and resource estimates",
        "Add COMMENT nodes to explain complex workflow sections and provide context",
        "Include TODO_LIST nodes with specific actionable items for parallel tasks that are directly related to the business plan",
        "Organize nodes in a logical flow with clear progression from one phase to the next"
      ],
      delegation_system: {
        role_assignment: "Assign each task to a specific role (e.g., 'Founder', 'Legal Counsel', 'Marketing Manager', 'Financial Advisor', 'HR Manager', 'Tech Lead', 'Sales Director')",
        priority_levels: ["CRITICAL", "HIGH", "MEDIUM", "LOW"],
        duration_estimation: "Include estimated duration for each task (be realistic: days, weeks, or months)",
        complex_node_handling: "For complex nodes, break down into subtasks with individual assignments"
      },
      document_requirements: {
        categories: {
          legal: ["Business licenses", "permits", "EIN", "operating agreements", "contracts", "NDAs"],
          financial: ["Business plan", "financial projections", "budget", "invoices", "tax forms"],
          hr: ["Job descriptions", "employment contracts", "onboarding materials", "policies"],
          marketing: ["Brand guidelines", "content calendar", "marketing plan", "campaign materials"],
          operational: ["SOPs", "vendor agreements", "inventory lists", "quality checklists"],
          compliance: ["Insurance policies", "health permits", "safety certifications"]
        }
      },
      todo_breakdown: {
        requirements: [
          "Clear, actionable task description",
          "Assigned role/person",
          "Realistic deadline",
          "Success criteria",
          "Mark completed tasks with completed: true"
        ],
        examples: [
          "Research 3 potential suppliers for raw materials by next Friday",
          "Schedule meeting with legal counsel to review partnership agreement",
          "Create social media accounts on Facebook, Instagram, and LinkedIn",
          "Set up project management tool and invite team members",
          "Prepare presentation deck for investor meeting"
        ]
      },
      available_node_types: availableNodeTypes,
      node_type_descriptions: {
        MARKET_RESEARCH: "Industry analysis, competitor research, customer surveys",
        REGISTER_BUSINESS: "Legal entity formation, registration, business structure",
        COMPLIANCE_CHECK: "Permits, licenses, regulatory requirements, insurance",
        HIRE_EMPLOYEE: "Recruitment, onboarding, team building",
        CREATE_WEBSITE: "Online presence, e-commerce, digital infrastructure",
        SOCIAL_POST: "Marketing campaigns, social media, content creation",
        MANAGE_INVOICES: "Financial operations, accounting, payment systems",
        EXPAND_BUSINESS: "Growth initiatives, scaling, new markets",
        TODO_LIST: "Groups of parallel tasks that can be done simultaneously with specific todo items that are directly related to the current workflow",
        DOCUMENT_NODE: "Document preparation, review, and organization",
        DELEGATION_NODE: "Task assignment and team coordination",
        NOVEL_NODE: "Innovative or unique elements specific to the business",
        COMMENT: "Explanatory notes that describe workflow sections, provide context, or explain complex decisions"
      },
      project_metadata: {
        total_estimated_timeline: "Calculate total estimated timeline",
        critical_path: "Identify critical path (sequence of tasks that determines minimum project duration)",
        key_milestones: "Define key milestones with target dates and deliverables",
        team_roles: "List all team roles with responsibilities and required skills",
        document_checklist: "Create comprehensive document checklist organized by category"
      },
      quality_standards: {
        label_format: "Each label must be a specific action statement (e.g., 'File Articles of Incorporation with State' not 'Legal Setup')",
        success_criteria: "Success criteria must be measurable (e.g., '50 customer interviews completed' not 'understand market')",
        dependencies: "Dependencies must be logical and explicit",
        timeline: "Timeline must account for approval processes, waiting periods, and realistic work capacity",
        comment_nodes: "Use COMMENT nodes to explain workflow sections, complex decisions, and provide context for better understanding",
        todo_lists: "Include TODO_LIST nodes with specific actionable items that can be tracked in the TODO panel and are directly related to the business plan"
      },
      error_free_workflow_requirements: {
        triggers_and_connections: "Ensure all nodes have proper triggers and connections",
        dependency_validation: "Validate that all dependencies are correctly specified",
        required_fields: "Confirm that all required fields are filled in",
        node_types: "Check that all node types are valid and appropriate for the task",
        workflow_completeness: "Verify that the workflow is complete and executable from start to finish",
        circular_dependencies: "Ensure there are no circular dependencies",
        team_roles: "Make sure all team roles are clearly defined",
        document_requirements: "Confirm that all document requirements are realistic and necessary",
        comment_usage: "Include COMMENT nodes to explain workflow logic and provide context",
        todo_inclusion: "Include relevant TODO_LIST nodes with actionable items for parallel tasks that are directly related to the current workflow"
      }
    }
  };

  const fullPrompt = `
${JSON.stringify(jsonPrompt, null, 2)}

User Request: "${prompt}"

Create a complete, executable, ERROR-FREE business plan that someone could follow step-by-step to build this business from concept to operation.

For TODO_LIST nodes, make sure to include specific, actionable todo items that are directly related to the business plan. For example, if the business is about opening a restaurant, include todo items like "Research local health department regulations", "Create menu with pricing", "Hire kitchen staff", etc.

For COMMENT nodes, provide helpful explanations about workflow sections or complex decisions.
`;

  try {
    console.log("Calling AI model for workflow generation with prompt:", prompt.substring(0, 100) + "...");
    
    const response = await ai.models.generateContent({
      model: "gemini-2.5-flash",
      contents: fullPrompt,
      config: {
        responseMimeType: "application/json",
        responseSchema: enhancedWorkflowSchema,
      },
    });

    console.log("Received response from AI model for workflow generation");
    
    const jsonString = response.text.trim();
    console.log("Response text:", jsonString.substring(0, 200) + "...");
    
    const parsedResponse = JSON.parse(jsonString);

    if (!parsedResponse.nodes || !Array.isArray(parsedResponse.nodes)) {
      throw new Error("AI response does not contain a valid nodes array.");
    }

    // Validate the structure
    const validatedNodes = parsedResponse.nodes.filter(item =>
      item && typeof item === 'object' && 
      'type' in item && 
      'label' in item && 
      (availableNodeTypes.includes(item.type) || item.type === 'COMMENT')
    );

    if (validatedNodes.length === 0 && parsedResponse.nodes.length > 0) {
      throw new Error("AI response contained invalid node types.");
    }

    // Process nodes to ensure they have the correct structure
    const processedNodes = validatedNodes.map(item => {
      const baseNode = {
        type: item.type,
        label: item.label,
        phase: item.phase || "PLANNING"
      };
      
      // Add type-specific fields
      if (item.type === 'COMMENT') {
        baseNode.text = item.text || item.label;
      } else if (item.type === 'TODO_LIST') {
        // Process TODO items for TODO_LIST nodes
        if (item.items && Array.isArray(item.items)) {
          baseNode.items = item.items.map(todo => ({
            text: todo.text || '',
            completed: todo.completed || false,
            assignedTo: todo.assignedTo || '',
            deadline: todo.deadline || ''
          }));
        } else if (item.todoItems && Array.isArray(item.todoItems)) {
          baseNode.items = item.todoItems.map(todo => ({
            text: todo.task || todo.text || '',
            completed: todo.completed || false,
            assignedTo: todo.assignedTo || '',
            deadline: todo.deadline || ''
          }));
        } else {
          baseNode.items = [];
        }
      } else if (item.assignedTo) {
        baseNode.assignedTo = item.assignedTo;
        baseNode.priority = item.priority || "MEDIUM";
        baseNode.estimatedDuration = item.estimatedDuration || "";
        baseNode.requiredDocuments = item.requiredDocuments || [];
        baseNode.todoItems = item.todoItems || [];
        baseNode.successCriteria = item.successCriteria || "";
      }
      
      return baseNode;
    });

    return {
      nodes: processedNodes,
      metadata: parsedResponse.projectMetadata || {},
    };
  } catch (error) {
    console.error("Error generating workflow from prompt:", error);
    // Provide more detailed error information
    if (error.message.includes("API key")) {
      throw new Error("Invalid API key. Please check your Gemini API configuration.");
    } else if (error.message.includes("quota")) {
      throw new Error("API quota exceeded. Please try again later or check your Gemini API plan.");
    } else if (error.message.includes("parse")) {
      throw new Error("Failed to parse AI response. The response format was unexpected.");
    } else {
      throw new Error(`Failed to communicate with the AI model: ${error.message}`);
    }
  }
}

export async function generateNodeFromPrompt(prompt) {
  // Convert the prompt to JSON format for better structure and clarity
  const jsonPrompt = {
    role: "system",
    content: {
      expertise: "business process automation assistant",
      task: "Analyze the user's request and create a detailed workflow node with complete information",
      required_elements: [
        "Choose the single best node type",
        "Assign to appropriate role (e.g., 'Founder', 'Legal Team', 'Marketing Manager', 'Operations Manager')",
        "Set realistic priority (CRITICAL, HIGH, MEDIUM, LOW)",
        "Estimate duration (e.g., '2 weeks', '3 days', '1 month')",
        "List ALL required documents for this task"
      ],
      document_categories: {
        legal_documents: ["contracts", "licenses", "registrations"],
        financial_documents: ["budgets", "projections", "invoices"],
        hr_documents: ["job descriptions", "policies"],
        marketing_materials: ["plans", "content", "guidelines"],
        operational_documents: ["SOPs", "checklists", "agreements"],
        compliance_documents: ["permits", "certifications", "insurance"]
      },
      quality_standards: {
        specificity: "Be specific and comprehensive",
        practicality: "Think about what someone would actually need to complete this task"
      },
      available_node_types: availableNodeTypes
    }
  };

  const fullPrompt = `
${JSON.stringify(jsonPrompt, null, 2)}

User Request: "${prompt}"
`;

  try {
    console.log("Calling AI model for node generation with prompt:", prompt.substring(0, 100) + "...");
    
    const response = await ai.models.generateContent({
      model: "gemini-2.5-flash",
      contents: fullPrompt,
      config: {
        responseMimeType: "application/json",
        responseSchema: singleNodeSchema,
      },
    });

    console.log("Received response from AI model for node generation");
    
    const jsonString = response.text.trim();
    console.log("Response text:", jsonString.substring(0, 200) + "...");
    
    const parsedResponse = JSON.parse(jsonString);

    if (
      parsedResponse &&
      typeof parsedResponse === 'object' &&
      'type' in parsedResponse &&
      'label' in parsedResponse &&
      'assignedTo' in parsedResponse &&
      'priority' in parsedResponse &&
      availableNodeTypes.includes(parsedResponse.type)
    ) {
      return parsedResponse;
    } else {
      throw new Error("AI response did not match the required schema.");
    }
  } catch (error) {
    console.error("Error generating node from prompt:", error);
    // Provide more detailed error information
    if (error.message.includes("API key")) {
      throw new Error("Invalid API key. Please check your Gemini API configuration.");
    } else if (error.message.includes("quota")) {
      throw new Error("API quota exceeded. Please try again later or check your Gemini API plan.");
    } else if (error.message.includes("parse")) {
      throw new Error("Failed to parse AI response. The response format was unexpected.");
    } else {
      throw new Error(`Failed to communicate with the AI model: ${error.message}`);
    }
  }
}